import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

// Build an embeddable src from a YouTube link, Google Drive share link, or direct video URL.
export const toEmbed = (url) => {
  if (!url) return { type: "none" };
  const u = url.trim();
  // YouTube
  const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0` };
  // Vimeo
  const vm = u.match(/vimeo\.com\/(\d+)/);
  if (vm) return { type: "iframe", src: `https://player.vimeo.com/video/${vm[1]}?autoplay=1` };
  // Google Drive
  const gd = u.match(/drive\.google\.com\/file\/d\/([\w-]+)/) || u.match(/drive\.google\.com\/open\?id=([\w-]+)/);
  if (gd) return { type: "iframe", src: `https://drive.google.com/file/d/${gd[1]}/preview` };
  // Direct video file
  if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(u)) return { type: "video", src: u };
  // Fallback: try as iframe
  return { type: "iframe", src: u };
};

export const VideoModal = ({ open, url, title, onClose }) => {
  const embed = toEmbed(url);
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[10001] bg-void/95 backdrop-blur-xl flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="video-modal"
        >
          <button onClick={onClose} data-testid="video-modal-close" className="absolute top-6 right-6 text-white/70 hover:text-gold transition-colors" aria-label="Close video">
            <X size={32} />
          </button>
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            {title && (
              <div className="mb-4 text-center" data-testid="video-modal-title">
                <p className="text-[0.6rem] sm:text-xs uppercase tracking-[0.35em] text-gold mb-2">Now Playing</p>
                <h3 className="font-display text-2xl sm:text-3xl uppercase tracking-wide text-offwhite leading-tight">{title}</h3>
              </div>
            )}
            <div className="w-full aspect-video border border-white/10 bg-black">
              {embed.type === "none" ? (
                <div className="w-full h-full flex flex-col items-center justify-center text-center px-6">
                  <p className="font-display text-4xl uppercase text-offwhite">Video Coming Soon</p>
                  <p className="mt-3 text-white/50 text-sm">Add a YouTube, Google Drive or uploaded video link for this association in the admin panel.</p>
                </div>
              ) : embed.type === "video" ? (
                <video src={embed.src} controls autoPlay className="w-full h-full" />
              ) : (
                <iframe className="w-full h-full" src={embed.src} title={title || "Association video"} allow="autoplay; encrypted-media; fullscreen; picture-in-picture" allowFullScreen />
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
